import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import * as Menu from '@radix-ui/react-menu'
import {
  useNoHoverMediaQuery,
  useTabletDownMediaQuery
} from 'utils/hooks/useMediaQueries'
import PlaylistActionsMenuContent, {
  actionPropTypes
} from './PlaylistActionsMenuContent'
import styles from './PlaylistActionsMenu.module.scss'

const createVirtualAnchor = ({ x, y }) => ({
  getBoundingClientRect: () => ({
    bottom: y,
    height: 0,
    left: x,
    right: x,
    top: y,
    width: 0,
    x,
    y,
    toJSON: () => ({})
  })
})

const PlaylistContextMenu = ({
  children,
  isPlayDisabled,
  onDownloadMp3,
  onDownloadPdf,
  onDownloadWav,
  onOpenChange,
  onPlay,
  onRemove
}) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const isTabletDown = useTabletDownMediaQuery()
  const hasNoHover = useNoHoverMediaQuery()
  const child = React.Children.only(children)
  const virtualAnchor = useMemo(
    () => createVirtualAnchor(position),
    [position]
  )
  const virtualRef = useRef(virtualAnchor)
  virtualRef.current = virtualAnchor

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const handleContextMenuCapture = (event) => {
    child.props.onContextMenuCapture?.(event)

    if (isTabletDown || hasNoHover) return

    event.preventDefault()
    event.stopPropagation()
    setPosition({ x: event.clientX, y: event.clientY })
    handleOpenChange(true)
  }

  const handleClick = (event) => event.stopPropagation()

  const handleCloseAutoFocus = (event) => event.preventDefault()

  return (
    <Menu.Root open={open} onOpenChange={handleOpenChange}>
      <Menu.Anchor virtualRef={virtualRef} />
      {React.cloneElement(child, {
        'data-context-menu-open': open,
        onContextMenuCapture: handleContextMenuCapture
      })}
      <Menu.Portal>
        <Menu.Content
          avoidCollisions
          align="start"
          className={styles.menuContent}
          collisionPadding={8}
          data-cy="playlist-context-menu"
          side="right"
          sideOffset={4}
          onClick={handleClick}
          onCloseAutoFocus={handleCloseAutoFocus}>
          <PlaylistActionsMenuContent
            isPlayDisabled={isPlayDisabled}
            Menu={Menu}
            onDownloadMp3={onDownloadMp3}
            onDownloadPdf={onDownloadPdf}
            onDownloadWav={onDownloadWav}
            onPlay={onPlay}
            onRemove={onRemove} />
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  )
}

PlaylistContextMenu.propTypes = {
  children: PropTypes.element.isRequired,
  isPlayDisabled: PropTypes.bool,
  onOpenChange: PropTypes.func,
  ...actionPropTypes
}

export default PlaylistContextMenu
